//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

// #region Imports
import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, Signer } from 'ethers'

import { ActivityEnum, AlertLevelEnum, PoolTypeEnum, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { PoolAbi, PoolDynamicAbi, PoolFlexAbi } from '@shared/Abis'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { ERC20Wei, erc20WeiToNumber, otAssert } from '@shared/conversions'

import { dateToEthDate, ProviderReadOnly } from '@shared/SchemaTypes'

import { AlertCTO } from '@shared/AlertCTO'

import { alertActions } from '@react/redux/alertSlice'

import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { Pool, PoolDynamic, PoolFlex } from '@typechain-types/index'
import { UserConnection } from '@shared/DeploymentConfigs'

export const redeemRequestFromPoolTx =
  (props: {
    poolAddr: EthAddr
    sharesInWei: ERC20Wei
    poolType: PoolTypeEnum
  }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const { poolAddr, sharesInWei, poolType } = props
    const activity: ActivityEnum = ActivityEnum.WITHDRAW_REQUEST

    const successMessage =
      poolType === PoolTypeEnum.Term
        ? 'Rollover Termination request submitted successfully'
        : 'Withdrawal request submitted successfully'
    try {
      const { webSocket } = getState().chainState
      const { chainConfigurationName, providerReadOnly } = getState().chainState.chainConfiguration
      otAssert(providerReadOnly, 'providerReadOnly')

      const { roleEnum, signer } = getState().chainState.userConnection as UserConnection
      assertRole(roleEnum, RoleEnum.LENDER)

      await redeemRequestFromPool(
        webSocket,
        poolType,
        signer as Signer,
        providerReadOnly,
        poolAddr,
        sharesInWei,
        chainConfigurationName
      )

      dispatch(poolActions.redeemRequestFromPoolSuccess(poolAddr))
      const requestSuccess: AlertCTO = {
        alertLevel: AlertLevelEnum.SUCCESS,
        title: successMessage,
        description: '',
        sourceObjectAddr: poolAddr,
        createdAt: dateToEthDate(new Date()),
        dueDateTime: 0,
      }
      dispatch(alertActions.addAlert(requestSuccess))
    } catch (e: any) {
      await dispatchError(dispatch, e, activity, 1006)
    }
  }
export const redeemRequestFromPool = async (
  webSocket: WebSocket | undefined,
  poolType: PoolTypeEnum,
  lenderSigner: Signer,
  _providerReadOnly: ProviderReadOnly,
  poolAddr: EthAddr,
  shares: ERC20Wei,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('redeemRequestFromPool', shares, poolAddr)

  const uuid = txUUID()
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.WITHDRAW_REQUEST, poolAddr, uuid, chainConfigurationName))
  // prettier-ignore
  /* eslint-disable prettier/prettier */
  const poolContract: Pool | PoolFlex | PoolDynamic =
    poolType == PoolTypeEnum.Term
      ? (new Contract(poolAddr, PoolAbi, lenderSigner) as unknown as Pool)
      : poolType == PoolTypeEnum.FlexibleRate
      ? (new Contract(poolAddr, PoolFlexAbi, lenderSigner) as unknown as PoolFlex)
      : (new Contract(poolAddr, PoolDynamicAbi, lenderSigner) as unknown as PoolDynamic)
  const accountAddr: EthAddr = await lenderSigner.getAddress()
  logOT.info('redeemRequestFromPool accountAddr', accountAddr, poolAddr)
  const maxRedeemRequest = await poolContract.maxRedeemRequest(accountAddr)
  logOT.info('redeemRequestFromPool maxRedeemRequest', maxRedeemRequest)
  if (maxRedeemRequest === 0n) {
    throw new Error('No redeem request available')
  }
  logOT.info('maxRedeemRequest', maxRedeemRequest.toString(), accountAddr)
  const maxRedeemNumber = erc20WeiToNumber(maxRedeemRequest.toString() as ERC20Wei)
  logOT.info('maxRedeemRequest maxRedeemNumber', maxRedeemNumber)
  let tx2
  if (erc20WeiToNumber(shares) > maxRedeemNumber) {
    tx2 = await poolContract.requestRedeem(maxRedeemRequest)
    logOT.debug('redeemFromPool redeemTx')
  } else {
    logOT.debug('maxRedeem', shares.toString(), accountAddr)
    tx2 = await poolContract.requestRedeem(shares)
    logOT.debug('redeemFromPool redeemTx')
  }

  webSocket?.send(
    wsServerActionPoolTX(ActivityEnum.WITHDRAW_REQUEST, poolAddr, uuid, tx2.hash as TxHash, chainConfigurationName)
  )
  await tx2.wait()
}
